import React, { useEffect, useMemo, useState } from 'react';
import { Select, Input, Radio, DatePicker, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';
import useXmlPattern from '../../utils/xmlPattern';
import useAuthContext from '../../contexts/AuthContext';
import useQuotesContext from './QuotesContext';
import useErrorMessage from '../../utils/ErrorMessage';
import { genders, representative } from '../../utils/constants/enums';
import { PersonIcon, EntityIcon } from '../../utils/constants/formIcons';
import {
  isValidZipcode,
  isMetropolitanZipcode,
  isCorseZipcode,
  isMetropolianAndMonacoZipcode
} from '../../utils/zipcode';
import { arrayConditionnalElement } from '../../utils/arrayConditionnalElement';
import { MRIWK_MRI_BUILDING_TYPES, isWKPno } from './mriwkFields';

const { Option } = Select;

export const dromComAllowedTypes = ['do', 'rcpmk', 'rcphx'];
let allowUpdateDataOnEditMode = false;

const useLastStepFields = (form) => {
  const { t } = useTranslation();
  const { subPath: type } = useParams();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const assoType = params.get('assoT');
  const supplier = params.get('sup');
  const { pathname } = location;
  const purpose = pathname.split('/')[3];
  const { dispatchAPI } = useAuthContext();
  const {
    inseeLoading,
    siretStatus,
    rnaStatus,
    formValues,
    setFormValues,
    exemptions,
    enums,
    errorGetCities,
    setErrorGetCities
  } = useQuotesContext();
  const xmlPattern = useXmlPattern();
  const { message } = useErrorMessage();
  const isAmendment = form.getFieldValue('previous_contract');
  const dateFormat = 'DD/MM/YYYY';

  const [cities, setCities] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [addressSuggestion, setAddressSuggestion] = useState([]);

  const postalCodeWatcher = Form.useWatch(
    ['customer', 'address', 'postal_code'],
    form
  );

  const dromComAllowed = useMemo(() => dromComAllowedTypes.includes(type), [
    type
  ]);

  const isAllowedZipcode = (value) => {
    if (dromComAllowed) {
      return true;
    }
    if (type === 'rcphx') {
      return isMetropolianAndMonacoZipcode(value);
    }
    if (type === 'rcngawk') {
      return isCorseZipcode(value) || isMetropolitanZipcode(value);
    }

    if ((type === 'rcp' && supplier === 'ml') || type === 'pnoml') {
      return isCorseZipcode(value) || isMetropolitanZipcode(value);
    }

    if (supplier === 'cm' && type === 'rcp' && value?.startsWith('13')) {
      return false;
    }
    return isMetropolitanZipcode(value);
  };

  const getAddressAutocomplete = async (address_autocomplete) => {
    if (address_autocomplete) {
      setIsLoading(true);
      try {
        const { data } = await dispatchAPI('GET', {
          url: `/places/${address_autocomplete}`
        });
        setAddressSuggestion(data?.predictions);
      } catch (e) {
        if (e.response) message(e.response.status);
      }
      setIsLoading(false);
    }
  };

  const getAddressPlaceId = async (placeId) => {
    setIsLoading(true);
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/places/placeid/${placeId}`
      });

      setFormValues({
        ...formValues,
        customer: {
          ...formValues?.customer,
          address: {
            ...formValues?.customer?.address,
            number: data.result.address_components[0].long_name,
            street: data.result.address_components[1].long_name,
            postal_code: data.result.address_components[6].long_name,
            city: data.result.address_components[2].long_name
          }
        }
      });
      form.setFieldsValue({
        customer: {
          address: {
            number: data.result.address_components[0].long_name,
            street: data.result.address_components[1].long_name,
            postal_code: data.result.address_components[6].long_name,
            city: data.result.address_components[2].long_name,
            address_autocomplete: ''
          }
        }
      });
    } catch (e) {
      if (e.response) message(e.response.status);
    }
    setIsLoading(false);
  };

  const getCity = async (zipCode) => {
    try {
      if (!isValidZipcode(zipCode) || !isAllowedZipcode(zipCode)) {
        return [];
      }

      const { data } = await dispatchAPI('GET', {
        url: `/insee/cities/zipcode/${zipCode}`
      });

      const formattedCities = data.map((city) => ({
        ...city,
        label: `${city.name} (${city.zipcode})`
      }));
      setErrorGetCities(false);
      return formattedCities;
    } catch (e) {
      if (e.response) {
        message(e.response.status);
      }
      const isNotFoundError = e.response?.status === 404;
      setErrorGetCities(!isNotFoundError);
      return [];
    }
  };

  const handleTrimValue = (e) => {
    const { value } = e.target;
    const newValue = value.trim();
    form.setFieldsValue({ customer: { email: newValue } });
  };

  const handlePostalCodeChange = async (value) => {
    const cityList = await getCity(value);
    setCities(cityList);

    if (!formValues?.customer?.address) {
      return;
    }

    if (!cityList?.length > 0) {
      if (formValues?.customer?.address?.city) {
        form.setFieldsValue({
          customer: {
            ...formValues.customer.address,
            address: {
              ...formValues.customer.address,
              city: null
            }
          }
        });
        setFormValues({
          ...formValues,
          customer: {
            ...formValues.customer,
            address: {
              ...formValues.customer.address,
              city: null
            }
          }
        });
      }
      return;
    }

    const matchedCity = cityList.find(
      (city) =>
        city.name?.toLowerCase() === formValues?.address?.city?.toLowerCase()
    );

    const newSelectedCity = matchedCity || cityList[0];

    const newFormValues = {
      ...formValues,
      customer: {
        ...(formValues.customer || {}),
        address: {
          ...(formValues.customer.address || {}),
          city: newSelectedCity.name
        }
      }
    };

    form.setFieldsValue({
      customer: {
        address: newFormValues.customer.address
      }
    });
    setFormValues(newFormValues);
  };

  useEffect(() => {
    // In edit mode, we want to handlePostalCodeChange only when the user udpdate it (and not the first time the form is updated with saved data)
    if (purpose !== 'edit' || allowUpdateDataOnEditMode) {
      handlePostalCodeChange(postalCodeWatcher);
    } else if (formValues) {
      allowUpdateDataOnEditMode = true;
    }
  }, [postalCodeWatcher]);

  const buildingName = useMemo(() => {
    if (type === 'mriml' || type === 'pnoml') {
      return formValues?.buildings?.[0]?.building_name || null;
    }
    if (type === 'mriwk') {
      return formValues?.address?.name || null;
    }
    return null;
  }, [formValues, type]);

  const buildingSiret = useMemo(() => {
    if (type === 'mriml' || type === 'pnoml') {
      return (
        (formValues?.buildings && formValues?.buildings[0]?.building_siret) ||
        null
      );
    }
    if (type === 'mriwk') {
      return formValues?.address?.siret || null;
    }
    return null;
  }, [formValues, type]);

  const subscriberTypes = useMemo(() => {
    if (!enums) {
      return [];
    }

    if (type === 'mriml') {
      return [
        ...enums?.subscriberTypes?.filter((f) =>
          f
            .toUpperCase()
            .startsWith(
              formValues?.subscriber === 'LEGAL_PERSON' ? 'LP_' : 'LE_'
            ) && f !== 'LE_BLDG_SELLER_OF_GOODS'
        ),
        'OTHER'
      ];
    }
    if (type === 'pnoml') {
      return [
        ...enums?.subscriberTypesPnoml?.filter((f) =>
          f
            .toUpperCase()
            .startsWith(
              formValues?.subscriber === 'LEGAL_PERSON' ? 'LP_' : 'LE_'
            )
        )
      ];
    }
    if (type === 'mriwk') {
      let baseArray = [...enums?.wakam_subscriber_types].filter(
        (f) => f !== 'OTHER'
      );

      if (isWKPno(formValues || {})) {
        baseArray = baseArray.filter((f) => f !== 'SYNDIC_VOLUNTEER');
      }

      return [...baseArray];
    }
    return [];
  }, [enums, type, formValues?.subscriber, formValues?.building_type]);

  const lastStepFields = [
    ...(type === 'mriml' || type === 'mriwk' || type === 'pnoml'
      ? [
          ...arrayConditionnalElement(
            [
              {
                name: ['building_name'],
                noLabel: true,
                input: (
                  <>
                    <strong>
                      {buildingName ? t(`${buildingName}`) : 'Aucune donnée'}
                    </strong>
                  </>
                ),
                displayOnly: true
              },
              {
                name: ['building_siret'],
                noLabel: true,
                input: (
                  <>
                    <strong>
                      {buildingSiret ? t(`${buildingSiret}`) : 'Aucune donnée'}
                    </strong>
                  </>
                ),
                displayOnly: true
              }
            ],
            type !== 'mriml' && type !== 'pnoml' && type !== 'mriwk'
          ),
          ...arrayConditionnalElement(
            [
              {
                name: ['property_name'],
                label: ['building_name'],
                input: <Input />
              },
              {
                name: ['property_siret'],
                label: ['building_siret'],
                input: <Input />
              }
            ],
            type === 'mriwk' &&
              MRIWK_MRI_BUILDING_TYPES.includes(formValues?.building_type)
          ),
          {
            name: ['subscriber'],
            rules: [{ required: true }],
            input: (
              <Radio.Group
                buttonStyle="solid"
                className="radio-with-icon-text-wrap radio-with-icon"
                onChange={() =>
                  form.setFieldsValue({
                    subscriber_type: undefined,
                    subscriber_type_other: undefined,
                    legal_entity_name: undefined
                  })
                }
              >
                <Radio.Button value="LEGAL_PERSON">
                  <PersonIcon />
                  Une personne physique
                </Radio.Button>
                <Radio.Button value="LEGAL_ENTITY">
                  <EntityIcon />
                  Une personne morale
                </Radio.Button>
              </Radio.Group>
            ),
            step: 0
          },
          ...(formValues && formValues.subscriber === 'LEGAL_ENTITY'
            ? [
                {
                  name: ['subscriber_type'],
                  rules: [{ required: true }],
                  input: (
                    <Select>
                      {subscriberTypes.map((e) => (
                        <Option value={e} key={e}>
                          {t(`contracts.tags.${e}`)}
                        </Option>
                      ))}
                    </Select>
                  ),
                  help: exemptions.includes('subscriber_type')
                    ? t('help.exemptions')
                    : null,
                  step: 0
                }
              ]
            : []),
          ...(formValues && formValues.subscriber === 'LEGAL_ENTITY'
            ? [
                {
                  name: ['legal_entity_name'],
                  rules: [{ required: true }],
                  input: <Input />,
                  step: 0
                }
              ]
            : []),
          ...(formValues && formValues?.subscriber_type === 'OTHER'
            ? [
                {
                  name: ['subscriber_type_other'],
                  rules: [{ required: true }],
                  input: <Input />,
                  step: 0
                }
              ]
            : [])
        ]
      : [
          ...(formValues?.customer_type !== 'INDIVIDUAL'
            ? [
                ...(type !== 'association'
                  ? [
                      {
                        name: ['customer', 'siret'],
                        label: ['customer', 'siret'],
                        input: <Input min={0} minLength={9} maxLength={14} />,
                        tooltip: true,
                        rules: [{ required: true }],
                        hasFeedback: true,
                        ...(inseeLoading
                          ? { validateStatus: 'validating' }
                          : {
                              ...(siretStatus || siretStatus === false
                                ? {
                                    validateStatus: !siretStatus
                                      ? 'error'
                                      : 'success'
                                  }
                                : {})
                            })
                      },
                      {
                        name: ['customer', 'company_creation_date'],
                        label: 'company_creation_date',
                        rules: [
                          {
                            required: type.toLowerCase() === 'rcngawk'
                          }
                        ],
                        input: <DatePicker format={dateFormat} />,
                        hidden: !(type.toLowerCase() === 'rcngawk')
                      }
                    ]
                  : [
                      {
                        name: ['customer', 'rna'],
                        label: ['customer', 'rna'],
                        input: <Input minLength={10} maxLength={10} />,
                        tooltip: true,
                        rules: [{ required: true }],
                        hasFeedback: true,
                        ...(inseeLoading
                          ? { validateStatus: 'validating' }
                          : {
                              ...(rnaStatus || rnaStatus === false
                                ? {
                                    validateStatus: !rnaStatus
                                      ? 'error'
                                      : 'success'
                                  }
                                : { validateStatus: 'error' })
                            })
                      }
                    ]),
                {
                  name: ['customer', 'naf'],
                  label: ['customer', 'naf'],
                  input: <Input style={{ width: 100 }} />,
                  hidden: true
                },
                {
                  name: ['customer', 'principal_activity'],
                  input: <Input style={{ width: 550 }} />,
                  hidden: true
                },
                {
                  name: ['customer', 'company_creation_date'],
                  label: assoType
                    ? 'association_creation_date'
                    : 'company_creation_date',
                  input: <DatePicker format={dateFormat} />,
                  hidden: !assoType
                },
                {
                  name: ['customer', 'company_name'],
                  label: assoType ? 'association_name' : 'company_name',
                  tooltip: true,
                  rules: [{ required: true }, ...xmlPattern]
                }
              ]
            : [])
        ]),
    ...(assoType
      ? [
          {
            name: ['customer', 'representative'],
            rules: [{ required: true }],
            input: (
              <Select>
                {representative &&
                  representative.map((g) => (
                    <Option value={g} key={g}>
                      {t(`insurers.form.${g}`)}
                    </Option>
                  ))}
              </Select>
            )
          }
        ]
      : []),
    {
      name: ['customer', 'gender'],
      rules: [{ required: true }],
      input: (
        <Select>
          {genders &&
            genders.map((g) => (
              <Option value={g} key={g}>
                {t(`insurers.form.${g}`)}
              </Option>
            ))}
        </Select>
      )
    },
    {
      name: ['customer', 'last_name'],
      rules: [{ required: true }, ...xmlPattern]
    },
    {
      name: ['customer', 'first_name'],
      rules: [{ required: true }, ...xmlPattern]
    },
    {
      name: ['customer', 'phone_number'],
      rules: [{ required: true }, { len: 10 }]
    },
    {
      name: ['customer', 'email'],
      tooltip: purpose === 'amendment' || isAmendment,
      rules: [{ required: true }, { type: 'email' }],
      input: (
        <Input
          disabled={purpose === 'amendment' || isAmendment}
          onChange={handleTrimValue}
        />
      )
    },
    {
      name: ['address_autocomplete'],
      label:
        type?.includes('mriml') || type?.includes('pnoml')
          ? 'address_autocomplete_mri'
          : 'address_autocomplete',
      input: (
        <Select
          filterOption={false}
          showSearch
          loading={isLoading}
          showArrow={false}
          notFoundContent={null}
          onSearch={getAddressAutocomplete}
          onSelect={async (selected) => {
            const placeId = selected.split('#')[0];
            await getAddressPlaceId(placeId);
          }}
          options={
            addressSuggestion &&
            addressSuggestion.map((predictions) => ({
              label: predictions.description,
              value: `${predictions.place_id}#${predictions.description}`
            }))
          }
          autoComplete="anyrandomstring"
        />
      )
    },
    {
      name: ['customer', 'address', 'number'],
      rules: [{ required: true }]
    },
    {
      name: ['customer', 'address', 'street'],
      rules: [{ required: true }]
    },
    {
      name: ['customer', 'address', 'additional']
    },
    {
      name: ['customer', 'address', 'postal_code'],
      rules: [
        { required: true },
        {
          validator: (_, value, callback) => {
            if (!isValidZipcode(value)) {
              callback('Code postal invalide');
            } else if (!isAllowedZipcode(value)) {
              callback('Code postal non autorisé');
            } else {
              callback();
            }
          }
        }
      ],
      input: (
        <Input style={{ width: '90px' }} autoComplete="off" minLength={5} />
      )
    },
    {
      name: ['customer', 'address', 'city'],
      rules: [{ required: true }],
      help: exemptions.includes('city') ? t('help.city') : null,
      input: !errorGetCities ? (
        <Select
          showSearch
          style={{ width: '250px' }}
          options={cities}
          fieldNames={{ value: 'name' }}
        />
      ) : (
        <Input style={{ width: '250px' }} fieldNames={{ value: 'name' }} />
      )
    }
  ];

  return { lastStepFields };
};

export default useLastStepFields;
